import React, { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Users, Wifi, WifiOff, Navigation, Navigation2, Search, Maximize2 } from 'lucide-react';
import api from '../../utils/api';
import { loadGoogleMaps } from '../../utils/googleMaps';
import ShiftActivityPanel from './ShiftActivityPanel';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const SOCKET_URL = process.env.NODE_ENV === 'production'
  ? window.location.origin
  : 'http://localhost:5000';

const TRAIL_MAX  = 5000;     // effectively uncapped — we draw the FULL shift route, not just a short tail
const STALE_WARN = 90_000;   // 90s → yellow dot (background GPS updates less often than foreground)
const STALE_ERR  = 360_000;  // 6min → red dot

const COLORS = ['#4f46e5','#059669','#dc2626','#d97706','#7c3aed','#0284c7','#db2777','#16a34a'];
function workerColor(staffId) {
  let h = 0;
  for (const c of (staffId || '')) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return COLORS[h % COLORS.length];
}

function initials(name) {
  return (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

function staleDot(ms) {
  if (ms > STALE_ERR)  return '#ef4444';
  if (ms > STALE_WARN) return '#f59e0b';
  return '#22c55e';
}

function makeIcon(color, name, staleMs) {
  const dot = staleDot(staleMs);
  return L.divIcon({
    className: '',
    html: `<div style="width:40px;height:40px;border-radius:50%;background:${color};border:3px solid #fff;
      display:flex;align-items:center;justify-content:center;color:#fff;font-size:13px;font-weight:800;
      box-shadow:0 3px 12px rgba(0,0,0,0.25);position:relative;">
      ${initials(name)}
      <span style="position:absolute;bottom:-3px;right:-3px;width:12px;height:12px;border-radius:50%;
        background:${dot};border:2px solid #fff;"></span>
    </div>`,
    iconSize: [40,40], iconAnchor: [20,20], popupAnchor: [0,-24],
  });
}

function agoText(ts) {
  if (!ts) return 'unknown';
  const s = Math.round((Date.now() - ts) / 1000);
  if (s < 10) return 'just now';
  if (s < 60) return `${s}s ago`;
  return `${Math.round(s/60)}m ago`;
}

function fmtTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hr = +h;
  return `${hr % 12 || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`;
}

function shiftPct(date, start, end) {
  if (!date || !start || !end) return null;
  const now = Date.now();
  const s = new Date(`${date}T${start}`).getTime();
  const e = new Date(`${date}T${end}`).getTime();
  if (now <= s) return 0;
  if (now >= e) return 100;
  return Math.round((now - s) / (e - s) * 100);
}

function buildPopup(w) {
  const staleMs = w.updatedAt ? Date.now() - w.updatedAt : 0;
  const speed   = w.speed != null ? (w.speed * 3.6).toFixed(1) : null;
  const moving  = w.speed != null && w.speed > 0.5;
  const prog    = shiftPct(w.shiftDate, w.shiftStart, w.shiftEnd);
  return `<div style="font-family:system-ui;padding:6px 2px;min-width:180px">
    <div style="font-weight:700;font-size:14px;color:#1e293b">${w.staffName || 'Worker'}</div>
    ${speed != null ? `<div style="font-size:11px;color:${moving ? '#4f46e5' : '#94a3b8'};font-weight:600;margin-top:2px">
      ${moving ? '&#9654; ' + speed + ' km/h' : '&#9679; Stationary'}</div>` : ''}
    ${w.shiftStart && w.shiftEnd ? `<div style="font-size:11px;color:#64748b;margin-top:4px">
      Shift: ${fmtTime(w.shiftStart)} &ndash; ${fmtTime(w.shiftEnd)}</div>` : ''}
    ${w.shiftAddress ? `<div style="font-size:11px;color:#64748b;margin-top:2px;max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">
      &#128205; ${w.shiftAddress}</div>` : ''}
    ${prog != null ? `<div style="margin-top:6px">
      <div style="display:flex;justify-content:space-between;font-size:10px;color:#94a3b8;margin-bottom:2px">
        <span>Shift progress</span><span>${prog}%</span></div>
      <div style="background:#e2e8f0;border-radius:4px;height:4px">
        <div style="background:#4f46e5;border-radius:4px;height:4px;width:${prog}%"></div></div>
    </div>` : ''}
    ${w.accuracy ? `<div style="font-size:10px;color:#94a3b8;margin-top:4px">Accuracy &plusmn;${Math.round(w.accuracy)}m</div>` : ''}
    <div style="font-size:10px;color:#94a3b8;margin-top:2px">Updated ${agoText(w.updatedAt)}</div>
  </div>`;
}

export default function LiveTrackingMap() {
  const mapEl        = useRef(null);
  const mapObj       = useRef(null);
  const markersRef   = useRef({});      // staffId → L.Marker
  const trailsRef    = useRef({});      // staffId → [[lat,lon], ...]
  const polylinesRef = useRef({});      // staffId → L.Polyline
  const socketRef    = useRef(null);
  const workersRef   = useRef([]);      // mirror of workers state for use in callbacks
  const engineRef    = useRef('leaflet'); // 'google' once the Maps JS API loads
  const gRef         = useRef(null);      // google.maps namespace
  const infoRef      = useRef(null);      // shared google InfoWindow
  const applyWorkerRef = useRef(() => {});
  const fitAllRef      = useRef(() => {});

  const [workers,   setWorkers]   = useState([]);
  const [connected, setConnected] = useState(false);
  const [search,    setSearch]    = useState('');
  const [mapsKey,   setMapsKey]   = useState(null); // null=loading, ''=none, key=google
  const [activityShift, setActivityShift] = useState(null); // { id, name } → opens dwell-time panel
  const [activeOnly, setActiveOnly] = useState(true);       // show moving workers; stationary only if none moving

  useEffect(() => { api.get('/maps-config').then(r => setMapsKey(r.data?.googleMapsKey || '')).catch(() => setMapsKey('')); }, []);

  // Merge a worker update into both ref and state, preserving rich REST data
  const mergeWorker = useCallback((w) => {
    const existing = workersRef.current.find(x => x.staffId === w.staffId);
    const merged   = existing ? { ...existing, ...w } : w;
    workersRef.current = [...workersRef.current.filter(x => x.staffId !== w.staffId), merged];
    setWorkers([...workersRef.current]);
    return merged;
  }, []);

  const dropWorker = useCallback((staffId) => {
    workersRef.current = workersRef.current.filter(x => x.staffId !== staffId);
    setWorkers([...workersRef.current]);
  }, []);

  // Init map once we know whether a Google key is configured.
  useEffect(() => {
    if (mapsKey === null || !mapEl.current) return;   // still loading the key
    let cancelled = false;

    if (mapsKey) {
      // ── Google Maps engine ──
      loadGoogleMaps(mapsKey).then((maps) => {
        if (cancelled || !mapEl.current) return;
        gRef.current = maps;
        engineRef.current = 'google';
        mapObj.current = new maps.Map(mapEl.current, {
          center: { lat: -33.8688, lng: 151.2093 }, zoom: 11,
          mapTypeControl: true, streetViewControl: false, fullscreenControl: false,
        });
        infoRef.current = new maps.InfoWindow();
        // Render anything already received before the map was ready.
        workersRef.current.forEach(applyWorkerRef.current);
        if (workersRef.current.length) fitAllRef.current();
      }).catch(() => {
        // Key bad / blocked → fall back to Leaflet so the page still works.
        if (!cancelled) initLeaflet();
      });
    } else {
      initLeaflet();
    }

    function initLeaflet() {
      engineRef.current = 'leaflet';
      // attributionControl stays ON: OpenStreetMap's licence requires the credit to be visible on
      // the map, and a small line in the corner is the price of tiles that need no API key.
      const map = L.map(mapEl.current, { zoomControl: true, attributionControl: true })
        .setView([-33.8688, 151.2093], 11);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19, attribution: '&copy; OpenStreetMap contributors', subdomains: 'abc',
      }).addTo(map);
      mapObj.current = map;
      workersRef.current.forEach(applyWorkerRef.current);
    }

    return () => {
      cancelled = true;
      if (engineRef.current === 'leaflet' && mapObj.current) { mapObj.current.remove(); }
      mapObj.current = null; markersRef.current = {}; polylinesRef.current = {}; trailsRef.current = {};
    };
  }, [mapsKey]);

  // Update dashed trail polyline for a worker
  const updateTrail = useCallback((staffId, lat, lon) => {
    if (!mapObj.current) return;
    const color = workerColor(staffId);
    if (!trailsRef.current[staffId]) trailsRef.current[staffId] = [];
    const trail = trailsRef.current[staffId];
    if (engineRef.current === 'google') {
      trail.push({ lat, lng: lon });
      if (trail.length > TRAIL_MAX) trail.shift();
      if (polylinesRef.current[staffId]) polylinesRef.current[staffId].setPath(trail);
      else if (trail.length >= 2) polylinesRef.current[staffId] = new gRef.current.Polyline({
        path: trail, strokeColor: color, strokeOpacity: 0.5, strokeWeight: 3, map: mapObj.current,
      });
      return;
    }
    trail.push([lat, lon]);
    if (trail.length > TRAIL_MAX) trail.shift();
    if (polylinesRef.current[staffId]) {
      polylinesRef.current[staffId].setLatLngs(trail);
    } else if (trail.length >= 2) {
      polylinesRef.current[staffId] = L.polyline(trail, {
        color, weight: 3, opacity: 0.45, dashArray: '6,6',
      }).addTo(mapObj.current);
    }
  }, []);

  // Replace a worker's trail with the FULL shift route (seeded from stored breadcrumbs) so the
  // live map shows the whole path from clock-in → now, not just the last few socket pings.
  const seedTrail = useCallback((staffId, points) => {
    if (!mapObj.current || !points || points.length < 2) return;
    const color = workerColor(staffId);
    if (engineRef.current === 'google') {
      const path = points.map(p => ({ lat: p.lat, lng: p.lng }));
      trailsRef.current[staffId] = path;
      if (polylinesRef.current[staffId]) polylinesRef.current[staffId].setPath(path);
      else polylinesRef.current[staffId] = new gRef.current.Polyline({
        path, strokeColor: color, strokeOpacity: 0.6, strokeWeight: 4, map: mapObj.current,
      });
    } else {
      const path = points.map(p => [p.lat, p.lng]);
      trailsRef.current[staffId] = path;
      if (polylinesRef.current[staffId]) polylinesRef.current[staffId].setLatLngs(path);
      else polylinesRef.current[staffId] = L.polyline(path, { color, weight: 4, opacity: 0.55 }).addTo(mapObj.current);
    }
  }, []);

  // Fetch a shift's stored breadcrumb trail (the full route so far) and paint it.
  const loadFullTrail = useCallback((staffId, shiftId) => {
    if (!shiftId) return;
    api.get(`/location/shift/${shiftId}/trail`).then(r => {
      const pts = (r.data || [])
        .map(p => ({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) }))
        .filter(p => !Number.isNaN(p.lat) && !Number.isNaN(p.lng));
      seedTrail(staffId, pts);
    }).catch(() => {});
  }, [seedTrail]);

  // Place or update a marker on the map
  const applyWorker = useCallback((w) => {
    if (!mapObj.current || w.lat == null || w.lon == null) return;
    const color   = workerColor(w.staffId);
    const staleMs = w.updatedAt ? Date.now() - w.updatedAt : 0;
    updateTrail(w.staffId, w.lat, w.lon);
    const html = buildPopup(w);
    if (engineRef.current === 'google') {
      const maps = gRef.current;
      const icon = {
        path: maps.SymbolPath.CIRCLE, scale: 11, fillColor: color, fillOpacity: 1,
        strokeColor: '#fff', strokeWeight: 3,
      };
      if (markersRef.current[w.staffId]) {
        const m = markersRef.current[w.staffId];
        m.setPosition({ lat: w.lat, lng: w.lon });
        m.setIcon(icon);
        m._html = html;
      } else {
        const m = new maps.Marker({
          position: { lat: w.lat, lng: w.lon }, map: mapObj.current, icon,
          label: { text: initials(w.staffName), color: '#fff', fontSize: '10px', fontWeight: '700' },
          title: w.staffName || 'Worker',
        });
        m._html = html;
        m.addListener('click', () => { infoRef.current.setContent(m._html); infoRef.current.open(mapObj.current, m); });
        markersRef.current[w.staffId] = m;
      }
      return;
    }
    if (markersRef.current[w.staffId]) {
      const m = markersRef.current[w.staffId];
      m.setLatLng([w.lat, w.lon]);
      m.setIcon(makeIcon(color, w.staffName, staleMs));
      m.setPopupContent(html);
    } else {
      markersRef.current[w.staffId] = L.marker([w.lat, w.lon], { icon: makeIcon(color, w.staffName, staleMs) })
        .addTo(mapObj.current)
        .bindPopup(html, { maxWidth: 240 });
    }
  }, [updateTrail]);
  applyWorkerRef.current = applyWorker;

  const removeMarker = useCallback((staffId) => {
    const m = markersRef.current[staffId];
    if (m) { if (engineRef.current === 'google') m.setMap(null); else m.remove(); }
    delete markersRef.current[staffId];
    const pl = polylinesRef.current[staffId];
    if (pl) { if (engineRef.current === 'google') pl.setMap(null); else pl.remove(); }
    delete polylinesRef.current[staffId];
    delete trailsRef.current[staffId];
  }, []);

  // Socket.io connection
  useEffect(() => {
    // Pass the JWT so the server can authorize + org-scope the admin live map (join:admin).
    const socket = io(SOCKET_URL, { transports: ['websocket', 'polling'], reconnectionDelay: 2000, auth: { token: localStorage.getItem('token') } });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      socket.emit('join:admin');
    });
    socket.on('disconnect', () => setConnected(false));

    socket.on('workers:snapshot', (snap) => {
      snap.forEach(w => {
        const merged = mergeWorker(w);
        applyWorker(merged);
        loadFullTrail(w.staffId, w.shiftId);
      });
      if (snap.length > 0) fitAllRef.current();
    });

    socket.on('worker:moved', (w) => {
      const merged = mergeWorker(w);
      applyWorker(merged);
    });

    socket.on('worker:offline', ({ staffId }) => {
      dropWorker(staffId);
      removeMarker(staffId);
    });

    return () => { socket.disconnect(); socketRef.current = null; };
  }, [applyWorker, mergeWorker, dropWorker, removeMarker, loadFullTrail]);

  // Refresh stale indicator dots on markers every 5s
  useEffect(() => {
    const t = setInterval(() => {
      if (engineRef.current === 'leaflet') {
        workersRef.current.forEach(w => {
          if (markersRef.current[w.staffId] && w.updatedAt) {
            const staleMs = Date.now() - w.updatedAt;
            markersRef.current[w.staffId].setIcon(makeIcon(workerColor(w.staffId), w.staffName, staleMs));
          }
        });
      }
      // Force panel re-render so "X ago" labels stay fresh
      setWorkers(ws => [...ws]);
    }, 5000);
    return () => clearInterval(t);
  }, []);

  // REST fallback — loads initial state AND enriches workers with shift metadata
  useEffect(() => {
    api.get('/location/active').then(r => {
      if (!r.data?.length) return;
      r.data.forEach(row => {
        const w = {
          staffId:      row.staff_id,
          staffName:    `${row.first_name} ${row.last_name}`,
          shiftId:      row.shift_id,
          lat:          parseFloat(row.latitude),
          lon:          parseFloat(row.longitude),
          accuracy:     row.accuracy ? parseFloat(row.accuracy) : null,
          heading:      row.heading  ? parseFloat(row.heading)  : null,
          speed:        row.speed    ? parseFloat(row.speed)    : null,
          updatedAt:    new Date(row.updated_at).getTime(),
          shiftDate:    row.shift_date,
          shiftStart:   row.start_time,
          shiftEnd:     row.end_time,
          shiftAddress: row.shift_address,
        };
        const merged = mergeWorker(w);
        applyWorker(merged);
        loadFullTrail(w.staffId, w.shiftId);   // paint the whole shift route, not just live pings
      });
      fitBoundsTo(workersRef.current);
    }).catch(() => {});
  }, [applyWorker, mergeWorker, loadFullTrail]);

  // Periodically re-pull the full breadcrumb trail for each active worker. Background GPS uploads
  // arrive in batches but only the latest point is broadcast live, so a refresh keeps the drawn
  // route complete (fills in the intermediate points between socket pings).
  useEffect(() => {
    const t = setInterval(() => {
      workersRef.current.forEach(w => { if (w.shiftId) loadFullTrail(w.staffId, w.shiftId); });
    }, 30_000);
    return () => clearInterval(t);
  }, [loadFullTrail]);

  const fitBoundsTo = useCallback((list) => {
    const pts = (list || []).filter(w => w.lat != null && w.lon != null);
    if (!mapObj.current || !pts.length) return;
    if (engineRef.current === 'google') {
      const b = new gRef.current.LatLngBounds();
      pts.forEach(w => b.extend({ lat: w.lat, lng: w.lon }));
      mapObj.current.fitBounds(b, 60);
      if (pts.length === 1) mapObj.current.setZoom(15);
    } else {
      mapObj.current.fitBounds(pts.map(w => [w.lat, w.lon]), { padding: [60,60], maxZoom: 14 });
    }
  }, []);
  fitAllRef.current = () => fitBoundsTo(workersRef.current);

  const focusWorker = (w) => {
    if (!mapObj.current || w.lat == null) return;
    if (engineRef.current === 'google') {
      mapObj.current.panTo({ lat: w.lat, lng: w.lon });
      mapObj.current.setZoom(16);
      const m = markersRef.current[w.staffId];
      if (m && infoRef.current) { infoRef.current.setContent(m._html || ''); infoRef.current.open(mapObj.current, m); }
    } else {
      mapObj.current.setView([w.lat, w.lon], 16, { animate: true, duration: 0.8 });
      markersRef.current[w.staffId]?.openPopup();
    }
  };

  const fitAll = () => fitBoundsTo(workersRef.current);

  const isMoving = (w) => w.speed != null && w.speed > 0.5;
  const anyMoving = workers.some(isMoving);
  const movingCount = workers.filter(isMoving).length;
  const filtered = workers
    .filter(w => !search || (w.staffName || '').toLowerCase().includes(search.toLowerCase()))
    // "Active" view: show moving workers only — but if NONE are moving, fall back to showing all
    // (so stationary workers still appear). "All" view shows everyone.
    .filter(w => !activeOnly || !anyMoving || isMoving(w))
    .sort((a, b) => (isMoving(b) - isMoving(a)) || (b.updatedAt || 0) - (a.updatedAt || 0));

  return (
    <div className="flex h-full overflow-hidden bg-slate-50">

      {/* ── Left panel ────────────────────────────────────────────────────── */}
      <div className="w-72 shrink-0 flex flex-col bg-white border-r border-slate-200 z-10">

        {/* Header */}
        <div className="px-4 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2 mb-1">
            <Navigation size={18} className="text-primary-500" />
            <h1 className="text-base font-800 text-slate-800">Live Tracking</h1>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className={`flex items-center gap-1.5 text-xs font-600 px-2.5 py-1 rounded-full ${
              connected ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'
            }`}>
              {connected ? <><Wifi size={11} /> Live</> : <><WifiOff size={11} /> Connecting…</>}
            </span>
            <span className="flex items-center gap-1 text-xs text-slate-500">
              <Users size={11} />{movingCount} moving · {workers.length} on shift
            </span>
          </div>
          {/* Active (moving) vs All toggle */}
          <div className="flex items-center gap-1 mt-2 bg-slate-100 rounded-lg p-0.5 w-max">
            {[['active', 'Active'], ['all', 'All']].map(([k, l]) => {
              const on = (k === 'active') === activeOnly;
              return (
                <button key={k} onClick={() => setActiveOnly(k === 'active')}
                  className={`text-xs font-600 px-2.5 py-1 rounded-md transition-colors ${on ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'}`}>
                  {l}
                </button>
              );
            })}
          </div>
          {/* Search */}
          <div className="relative mt-3">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search workers…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg bg-slate-50 focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
        </div>

        {/* Worker list */}
        <div className="flex-1 overflow-y-auto py-2">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-6">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                <MapPin size={22} className="text-slate-400" />
              </div>
              <div>
                <p className="text-sm font-600 text-slate-600">
                  {search ? 'No matching workers' : 'No workers on shift'}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  {search
                    ? 'Try a different name'
                    : 'Workers appear here when they clock in and grant location access'}
                </p>
              </div>
            </div>
          ) : (
            filtered.map(w => {
              const color    = workerColor(w.staffId);
              const staleMs  = w.updatedAt ? Date.now() - w.updatedAt : Infinity;
              const isStale  = staleMs > STALE_WARN;
              const isVStale = staleMs > STALE_ERR;
              const dotCls   = isVStale ? 'bg-red-400' : isStale ? 'bg-amber-400' : 'bg-emerald-400 animate-pulse';
              const labelCls = isVStale ? 'text-red-500' : isStale ? 'text-amber-500' : 'text-emerald-600';
              const moving   = w.speed != null && w.speed > 0.5;
              const kmh      = w.speed != null ? (w.speed * 3.6).toFixed(1) : null;
              const prog     = shiftPct(w.shiftDate, w.shiftStart, w.shiftEnd);

              return (
                <div
                  key={w.staffId}
                  className="w-full flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0"
                >
                  <button onClick={() => focusWorker(w)} className="flex items-start gap-3 flex-1 min-w-0 text-left">
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 relative mt-0.5"
                       style={{ backgroundColor: color }}>
                    <span className="text-white text-xs font-800">{initials(w.staffName)}</span>
                    <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${dotCls}`} />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-700 text-slate-800 truncate">{w.staffName || 'Unknown'}</p>

                    {/* Clear status: Moving / Online·Stationary / Offline (lost signal) */}
                    <p className={`text-xs font-500 flex items-center gap-1 mt-0.5 ${labelCls}`}>
                      <span className={`w-1.5 h-1.5 rounded-full inline-block ${dotCls.split(' ')[0]}`} />
                      {isVStale
                        ? `Offline · last seen ${agoText(w.updatedAt)}`
                        : moving
                          ? `Moving · ${kmh} km/h`
                          : `Online · Stationary`}
                    </p>

                    {/* Shift times */}
                    {w.shiftStart && w.shiftEnd && (
                      <p className="text-xs text-slate-400 mt-0.5">
                        {fmtTime(w.shiftStart)} – {fmtTime(w.shiftEnd)}
                      </p>
                    )}

                    {/* Shift progress bar */}
                    {prog != null && (
                      <div className="mt-1.5">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-[10px] text-slate-400">Progress</span>
                          <span className="text-[10px] text-slate-500 font-600">{prog}%</span>
                        </div>
                        <div className="h-1 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500"
                               style={{ width: `${prog}%`, backgroundColor: color }} />
                        </div>
                      </div>
                    )}
                  </div>

                  </button>
                  {w.shiftId && (
                    <button onClick={() => setActivityShift({ id: w.shiftId, name: w.staffName })}
                      title="Visits & time at location"
                      className="shrink-0 mt-1 p-1.5 rounded-lg text-slate-400 hover:text-primary-600 hover:bg-primary-50">
                      <Navigation2 size={15} />
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-slate-100 space-y-2">
          {workers.length > 0 && (
            <button
              onClick={fitAll}
              className="w-full flex items-center justify-center gap-1.5 text-xs font-600 text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg py-1.5 transition-colors"
            >
              <Maximize2 size={12} /> Fit all workers
            </button>
          )}
          <p className="text-[11px] text-slate-400 text-center">
            Updates live · Route shows the full shift path (clock-in → now)
          </p>
          <div className="flex items-center justify-center gap-3 text-[10px] text-slate-400">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" />Live</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />&gt;30s</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400 inline-block" />&gt;2min</span>
          </div>
        </div>
      </div>

      {/* ── Map ──────────────────────────────────────────────────────────── */}
      <div ref={mapEl} className="flex-1" style={{ zIndex: 0 }} />

      {activityShift && (
        <ShiftActivityPanel shiftId={activityShift.id} title={activityShift.name} onClose={() => setActivityShift(null)} />
      )}
    </div>
  );
}
